import { CashFlow } from '@webcarrot/xirr';
import { ColDef, ColGroupDef, IAggFunc, IAggFuncParams } from 'ag-grid-community';
import { IInvestmentDataModel } from '../../../../../data-models/investment.data-model';
import { IMetricsDataModel } from '../../../../../data-models/metrics.data-model';
import { IRoundDataModel } from '../../../../../data-models/round.data-model';
import { MetricsTransactionDataModel } from '../../../../../schemas/MetricsTransaction.schema';
import { ColumnMeta } from '../../../../../types';
import { isObject } from '../../../../../util/object-util';
import { startOfDay } from 'date-fns';
import { transactionsToCashflows } from '@foresightdata/metrics-calculator';
import { calculatePoolIrr } from '../../../providers/calculatePoolIrr';
import { FDIterator } from '../StandardIterators';

export function runAggregationIterator<ReturnType, ValueType, ContextType = ValueType>(
  params: IAggFuncParams,
  iterator: FDIterator<ReturnType, ValueType, ContextType>,
  context?: ContextType
) {
  // v35 path. `aggregatedChildren` is the immediate children of the group
  // currently being aggregated: leaf rows expose `data`; child groups expose
  // `aggData[colId]` populated from this aggregator's earlier return. The
  // moic/irr iterators read numeric fields like `totalValue` / `amountInvested`
  // / `fmv`, which are present on both raw IMetricsDataModel rows AND on the
  // iterator's own return shape — so both branches feed `iterator.next` the
  // same way.
  //
  // In v33 we keyed off `params.rowNode.leafGroup` + `params.values`. v35
  // changed `params.values` to be raw cell values (numbers / undefined) for
  // leaf-group children, breaking that contract. Switching to
  // `aggregatedChildren` is the v35-native fix. The legacy branch below stays
  // as a fallback so test fixtures that mock `values` directly still work.
  if (params.aggregatedChildren) {
    const colId = params.column.getColId();
    for (const child of params.aggregatedChildren) {
      const prior = child.aggData?.[colId];
      if (prior != null) {
        iterator.next(prior as ValueType, context);
      } else if (child.data != null) {
        iterator.next(child.data as ValueType, context);
      }
    }
  } else if (params.rowNode?.leafGroup && params.rowNode.childrenAfterFilter) {
    for (const node of params.rowNode.childrenAfterFilter) {
      if (node.data == null) {
        continue;
      }
      iterator.next(node.data, context);
    }
  } else {
    for (const val of params.values) {
      if (val == null) {
        continue;
      }
      iterator.next(val, context);
    }
  }

  return iterator.value;
}

type OwnershipFromSharesAggResult = {
  sharesByCompanyId: Map<number, number>;
  latestFdsByCompanyId: Map<number, number>;
  latestDateByCompanyId: Map<number, string>;
  latestRoundByCompanyId: Map<number, number | null>;
  value: number | null;
  /** True iff at least one row carried a numeric fullyDilutedShares value (including 0). */
  hasFdsNumber: boolean;
};

/**
 * Computes company-level ownership as sum(noOfShares) / latestRound.fullyDilutedShares * 100.
 * At multi-company group levels, sums per-company ownership percentages.
 */
export function ownershipFromSharesAggFunc(
  params: IAggFuncParams<IInvestmentDataModel>
): OwnershipFromSharesAggResult {
  const roundsById = params.context.roundsById;
  const sharesByCompanyId = new Map<number, number>();
  const latestFdsByCompanyId = new Map<number, number>();
  const latestDateByCompanyId = new Map<number, string>();
  const latestRoundByCompanyId = new Map<number, number | null>();
  let hasFdsNumber = false;

  const considerLatest = (companyId: number, investmentDate: string, roundId: number | null, fds: number) => {
    const isLatest =
      investmentDataComparator(
        { investmentDate, roundId },
        {
          investmentDate: latestDateByCompanyId.get(companyId) ?? '',
          roundId: latestRoundByCompanyId.get(companyId) ?? null,
        },
        roundsById
      ) >= 0;
    if (isLatest) {
      latestDateByCompanyId.set(companyId, investmentDate);
      latestRoundByCompanyId.set(companyId, roundId);
      latestFdsByCompanyId.set(companyId, fds);
    }
  };

  const mergePriorResult = (prior: OwnershipFromSharesAggResult) => {
    if (prior.hasFdsNumber) {
      hasFdsNumber = true;
    }
    for (const [companyId, shares] of prior.sharesByCompanyId) {
      sharesByCompanyId.set(companyId, (sharesByCompanyId.get(companyId) ?? 0) + shares);
    }
    for (const [companyId, fds] of prior.latestFdsByCompanyId) {
      considerLatest(
        companyId,
        prior.latestDateByCompanyId.get(companyId) ?? '',
        prior.latestRoundByCompanyId.get(companyId) ?? null,
        fds
      );
    }
  };

  const visitLeafData = (data: IInvestmentDataModel) => {
    const { companyId, investmentDate, roundId, noOfShares, fullyDilutedShares } = data;
    if (!companyId) {
      return;
    }
    // `typeof 0 === 'number'` — intentional: FDS = 0 counts as "data present",
    // so the aggregator still renders `0.00%` (product decision). FDS = null or
    // the field being absent (IMetricsDataModel on PORTFOLIO_OVERVIEW) does not
    // qualify — those cases render blank via the guard below.
    if (typeof fullyDilutedShares === 'number') {
      hasFdsNumber = true;
    }
    // Include positive and negative noOfShares (restructures net against prior rounds).
    // Matches the `sum` aggFunc's semantics used by the displayed Number of Shares column.
    if (noOfShares) {
      sharesByCompanyId.set(companyId, (sharesByCompanyId.get(companyId) ?? 0) + noOfShares);
    }
    if (fullyDilutedShares != null && fullyDilutedShares > 0) {
      considerLatest(companyId, investmentDate, roundId, fullyDilutedShares);
    }
  };

  // v35 path. See `latestValueAggFunc` for the equivalent rationale — leaf
  // children arrive as `child.data`, group children as
  // `child.aggData[colId]`. v33 / test fallback paths preserved below.
  if (params.aggregatedChildren) {
    const colId = params.column.getColId();
    for (const child of params.aggregatedChildren) {
      const prior = child.aggData?.[colId] as OwnershipFromSharesAggResult | undefined;
      if (prior?.sharesByCompanyId instanceof Map) {
        mergePriorResult(prior);
      } else if (child.data) {
        visitLeafData(child.data);
      }
    }
  } else if (params.rowNode?.leafGroup && params.rowNode.childrenAfterFilter) {
    for (const node of params.rowNode.childrenAfterFilter) {
      if (!node.data) {
        continue;
      }
      visitLeafData(node.data);
    }
  } else {
    for (const child of params.values) {
      const prior = child as OwnershipFromSharesAggResult | null | undefined;
      if (prior?.sharesByCompanyId instanceof Map) {
        mergePriorResult(prior);
      }
    }
  }

  // Render blank (null) when rows carried noOfShares but no numeric
  // fullyDilutedShares was seen on any row — e.g. IMetricsDataModel rows in
  // PORTFOLIO_OVERVIEW / RETURN_FORECAST (FDS field absent), or ROUND_TRACKER
  // where every round has FDS=null (missing data). FDS=0 on any row still
  // counts as "data present" → falls through to the 0.00% path below.
  let value: number | null;
  if (!hasFdsNumber && sharesByCompanyId.size > 0) {
    value = null;
  } else {
    value = 0;
    for (const [companyId, shares] of sharesByCompanyId) {
      const fds = latestFdsByCompanyId.get(companyId);
      if (fds && fds > 0 && shares > 0) {
        value += (shares / fds) * 100;
      }
    }
  }

  return {
    sharesByCompanyId,
    latestFdsByCompanyId,
    latestDateByCompanyId,
    latestRoundByCompanyId,
    value,
    hasFdsNumber,
  };
}

type LatestValueAggResult = {
  latestValueByCompanyId: Map<number, number>;
  latestDateByCompanyId: Map<number, string>;
  latestRoundByCompanyId: Map<number, number | null>;
  value: number;
};
export function latestValueAggFunc(params: IAggFuncParams<IInvestmentDataModel>) {
  const valueField = params.colDef.field;
  if (typeof valueField !== 'string') {
    return null;
  }
  const roundsById = params.context.roundsById;
  const latestDateByCompanyId = new Map<number, string>();
  const latestRoundByCompanyId = new Map<number, number | null>();
  const latestValueByCompanyId = new Map<number, number>();

  const considerCandidate = (
    companyId: number,
    investmentDate: string,
    roundId: number | null,
    value: number
  ) => {
    const isLatest =
      investmentDataComparator(
        { investmentDate, roundId },
        {
          investmentDate: latestDateByCompanyId.get(companyId) ?? '',
          roundId: latestRoundByCompanyId.get(companyId) ?? null,
        },
        roundsById
      ) >= 0;
    if (isLatest) {
      latestDateByCompanyId.set(companyId, investmentDate);
      latestRoundByCompanyId.set(companyId, roundId);
      latestValueByCompanyId.set(companyId, value);
    }
  };

  const mergePriorResult = (prior: LatestValueAggResult) => {
    for (const [companyId, value] of prior.latestValueByCompanyId) {
      considerCandidate(
        companyId,
        prior.latestDateByCompanyId.get(companyId) ?? '',
        prior.latestRoundByCompanyId.get(companyId) ?? null,
        value
      );
    }
  };

  const visitLeafData = (data: IInvestmentDataModel) => {
    const { companyId, investmentDate, roundId } = data;
    if (!companyId) {
      return;
    }
    const value = data[valueField as keyof IInvestmentDataModel] as number | null;
    if (value != null) {
      considerCandidate(companyId, investmentDate, roundId, value);
    }
  };

  // v35 path. `aggregatedChildren` exposes leaf rows for leaf groups and child
  // groups (with prior `aggData[colId]`) for non-leaf groups. The legacy
  // `params.values` shape changed in v35 — leaf-group children now arrive as
  // raw cell values, not aggregated-result objects, which is why the v33
  // non-leaf branch crashed on `curr.latestValueByCompanyId.keys()`. Legacy
  // branches below remain so test fixtures that mock `values` /
  // `childrenAfterFilter` still pass.
  if (params.aggregatedChildren) {
    const colId = params.column.getColId();
    for (const child of params.aggregatedChildren) {
      const prior = child.aggData?.[colId] as LatestValueAggResult | undefined;
      if (prior?.latestValueByCompanyId instanceof Map) {
        mergePriorResult(prior);
      } else if (child.data) {
        visitLeafData(child.data);
      }
    }
  } else if (params.rowNode?.leafGroup && params.rowNode.childrenAfterFilter) {
    for (const node of params.rowNode.childrenAfterFilter) {
      if (!node.data) {
        return null;
      }
      visitLeafData(node.data);
    }
  } else {
    for (const curr of params.values as (LatestValueAggResult | null | undefined)[]) {
      if (curr?.latestValueByCompanyId instanceof Map) {
        mergePriorResult(curr);
      }
    }
  }

  let sum = 0;
  for (const value of latestValueByCompanyId.values()) {
    sum += value;
  }

  return {
    latestValueByCompanyId,
    latestDateByCompanyId,
    latestRoundByCompanyId,
    value: sum,
  } as LatestValueAggResult;
}

interface InvestmentData extends Pick<IInvestmentDataModel, 'investmentDate' | 'roundId'> {}
export function investmentDataComparator(
  a: InvestmentData,
  b: InvestmentData,
  roundsById: Map<number, IRoundDataModel>
) {
  // missing dates / rounds are considered the earliest
  if (a.investmentDate === b.investmentDate) {
    return (
      (roundsById.get(a.roundId ?? -1)?.sortOrder ?? Number.MIN_SAFE_INTEGER) -
      (roundsById.get(b.roundId ?? -1)?.sortOrder ?? Number.MIN_SAFE_INTEGER)
    );
  }
  return new Date(a.investmentDate || 0).getTime() - new Date(b.investmentDate || 0).getTime();
}

type NumericalFields<T> = {
  [K in keyof T as T[K] extends number | null | undefined ? K : never]: T[K];
};

type MoicAggResult = Partial<NumericalFields<IMetricsDataModel>>;
export const moicIterator = (
  totalValueField: keyof NumericalFields<IMetricsDataModel>,
  investmentAmountField: keyof NumericalFields<IMetricsDataModel>
): FDIterator<MoicAggResult & { value: number | null }, MoicAggResult> => {
  let sumTotalValue = 0;
  let sumInvestmentAmount = 0;

  return {
    get value() {
      const moic = sumInvestmentAmount ? sumTotalValue / sumInvestmentAmount : null;

      return {
        [totalValueField]: sumTotalValue,
        [investmentAmountField]: sumInvestmentAmount,
        value: moic,
      };
    },
    next(data) {
      sumTotalValue += data[totalValueField] ?? 0;
      sumInvestmentAmount += data[investmentAmountField] ?? 0;
    },
  };
};

type RealizedMoicAggResult = Partial<
  Pick<
    IMetricsDataModel,
    | 'distributions'
    | 'realizedAmountWithEscrow'
    | 'realizedValue'
    | 'cashInterestReceived'
    | 'cashDividendReceived'
  >
>;
// Realized MOIC nets cash income (cash interest + cash dividends) out of the numerator —
// it is income, not gain — mirroring metrics-calculator's realizedProceeds. Raw sums are
// returned (not the netted numerator) so a parent group re-nets exactly once.
export const realizedMoicIterator = (
  numeratorField: 'distributions' | 'realizedAmountWithEscrow'
): FDIterator<RealizedMoicAggResult & { value: number | null }, RealizedMoicAggResult> => {
  let sumNumerator = 0;
  let sumRealizedValue = 0;
  let sumCashInterest = 0;
  let sumCashDividend = 0;

  return {
    get value() {
      const netNumerator = sumNumerator - sumCashInterest - sumCashDividend;
      return {
        [numeratorField]: sumNumerator,
        realizedValue: sumRealizedValue,
        cashInterestReceived: sumCashInterest,
        cashDividendReceived: sumCashDividend,
        value: sumRealizedValue ? netNumerator / sumRealizedValue : null,
      };
    },
    next(data) {
      sumNumerator += data[numeratorField] ?? 0;
      sumRealizedValue += data.realizedValue ?? 0;
      sumCashInterest += data.cashInterestReceived ?? 0;
      sumCashDividend += data.cashDividendReceived ?? 0;
    },
  };
};

type IrrAggResult = Pick<IMetricsDataModel, 'fmv' | 'transactions'>;
export const irrIterator = (
  asOfDate = new Date()
): FDIterator<IrrAggResult & { value: number }, IrrAggResult> => {
  const groupCashflows: CashFlow[] = [];
  const groupedTransactions: MetricsTransactionDataModel[] = [];
  let fmv = 0;

  return {
    get value() {
      const irr = calculatePoolIrr(groupCashflows, fmv, startOfDay(asOfDate)) ?? Number.NaN;

      return {
        fmv,
        transactions: groupedTransactions,
        value: irr,
      };
    },
    next(data) {
      fmv += data.fmv;

      if (data.transactions) {
        for (const transaction of data.transactions) {
          groupedTransactions.push(transaction);
        }
        for (const cashflow of transactionsToCashflows(data.transactions)) {
          groupCashflows.push(cashflow);
        }
      }
    },
  };
};

export const customAggFuncs: { [key: string]: IAggFunc } = {
  moic: (params: IAggFuncParams) =>
    runAggregationIterator(params, moicIterator('totalValue', 'amountInvested')),
  irr: (params: IAggFuncParams) => runAggregationIterator(params, irrIterator(params.context.asOfDate)),
  latestValue: latestValueAggFunc,
  ownerShipPercentage: ownershipFromSharesAggFunc,
  moicAggFunc: (params: IAggFuncParams) =>
    runAggregationIterator(params, moicIterator('totalValue', 'amountInvested')),
  moicGeneric: (params: IAggFuncParams) => {
    const field = params.colDef.field;
    const moicConfig = field ? params.context?.aggFuncs?.moic?.[field] : undefined;
    if (!moicConfig) {
      return null;
    }
    return runAggregationIterator(
      params,
      moicIterator(
        moicConfig.totalValueFieldPath as keyof NumericalFields<IMetricsDataModel>,
        moicConfig.investmentAmountFieldPath as keyof NumericalFields<IMetricsDataModel>
      )
    );
  },
  moicTotalCost: (params: IAggFuncParams) =>
    runAggregationIterator(params, moicIterator('totalValue', 'totalCost')),
  netMoic: (params: IAggFuncParams) =>
    runAggregationIterator(params, moicIterator('netTotalValue', 'amountInvested')),
  unrealizedMOIC: (params: IAggFuncParams) =>
    runAggregationIterator(params, moicIterator('fmv', 'unrealizedValue')),
  realizedMOIC: (params: IAggFuncParams) =>
    runAggregationIterator(params, realizedMoicIterator('distributions')),
  realizedMOICWithEscrow: (params: IAggFuncParams) =>
    runAggregationIterator(params, realizedMoicIterator('realizedAmountWithEscrow')),
  irrAggFunc: (params: IAggFuncParams) =>
    runAggregationIterator(params, irrIterator(params.context.asOfDate)),
};

const aggNumericalComparator: ColDef['comparator'] = (a, b) => {
  const valueA = isObject(a) ? a.value : a;
  const valueB = isObject(b) ? b.value : b;

  if (valueA === valueB) {
    return 0;
  }
  if (valueA === null || valueA === undefined) {
    return -1;
  }
  if (valueB === null || valueB === undefined) {
    return 1;
  }
  return valueA - valueB;
};

const customAggFuncColumnsMap: Record<string, ColDef | ColGroupDef> = {
  latestValue: {
    aggFunc: 'latestValue',
    comparator: aggNumericalComparator,
    defaultAggFunc: 'latestValue',
    allowedAggFuncs: ['latestValue'],
  },
  moic: {
    aggFunc: 'moic',
    comparator: aggNumericalComparator,
    allowedAggFuncs: ['moic'],
  },
  moicTotalCost: {
    aggFunc: 'moicTotalCost',
    comparator: aggNumericalComparator,
    allowedAggFuncs: ['moicTotalCost'],
  },
  netMoic: {
    aggFunc: 'netMoic',
    comparator: aggNumericalComparator,
    defaultAggFunc: 'netMoic',
    allowedAggFuncs: ['netMoic'],
  },
  unrealizedMOIC: {
    aggFunc: 'unrealizedMOIC',
    comparator: aggNumericalComparator,
    defaultAggFunc: 'unrealizedMOIC',
    allowedAggFuncs: ['unrealizedMOIC', 'avg'],
  },
  realizedMOIC: {
    aggFunc: 'realizedMOIC',
    comparator: aggNumericalComparator,
    defaultAggFunc: 'realizedMOIC',
    allowedAggFuncs: ['realizedMOIC'],
  },
  realizedMOICWithEscrow: {
    aggFunc: 'realizedMOICWithEscrow',
    comparator: aggNumericalComparator,
    defaultAggFunc: 'realizedMOICWithEscrow',
    allowedAggFuncs: ['realizedMOICWithEscrow'],
  },
  moicGeneric: {
    aggFunc: 'moicGeneric',
    comparator: aggNumericalComparator,
    allowedAggFuncs: ['moicGeneric'],
  },
  irr: {
    aggFunc: 'irr',
    allowedAggFuncs: ['irr'],
    comparator: aggNumericalComparator,
  },
  ownerShipPercentage: {
    aggFunc: 'ownerShipPercentage',
    enableValue: true,
    allowedAggFuncs: ['ownerShipPercentage'],
    defaultAggFunc: 'ownerShipPercentage',
    comparator: aggNumericalComparator,
  },
};

export const defaultAggFunctions = ['avg', 'count', 'first', 'last', 'max', 'min', 'sum'];

export function getAggregationConfigs(column: ColumnMeta): Partial<ColDef | ColGroupDef> | null {
  if (!column.aggregation) {
    return null;
  }

  if (!column.aggFunc) {
    return {
      aggFunc: 'avg',
      enableValue: true,
      allowedAggFuncs: defaultAggFunctions, // without this, the agg menu includes all custom aggFuncs too
    };
  }

  if (customAggFuncColumnsMap[column.aggFunc]) {
    return customAggFuncColumnsMap[column.aggFunc];
  }

  return {
    aggFunc: column.aggFunc,
    enableValue: true,
    allowedAggFuncs: defaultAggFunctions,
  };
}
